/* eslint-disable @typescript-eslint/naming-convention */
import React, {
  createContext,
  useCallback,
  useMemo,
  useState,
  useContext,
} from 'react';

import api from '../services/api';
import { UserDTO } from '../dtos/UserDTO';

interface AuthState {
  access_token: string;
  user: UserDTO;
}

interface SignInData {
  email: string;
  password: string;
}

interface AuthContextData {
  user: UserDTO;
  signIn(credentials: SignInData): Promise<void>;
  signOut(): void;
  updateUser(user: UserDTO): void;
  storeData(token: string): Promise<void>;
  getUser(): Promise<void>;
  isLogged: boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const access_token = localStorage.getItem('@IndikeGanheCRM:token');
    const user = localStorage.getItem('@IndikeGanheCRM:user');

    if (access_token && user) {
      return { access_token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const getUser = useCallback(async () => {
    const access_token = localStorage.getItem('@IndikeGanheCRM:token');

    if (!access_token) return;

    const userResponse = await api.get<UserDTO>('/admin/auth/user', {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    });
    const user = userResponse.data;

    localStorage.setItem('@IndikeGanheCRM:user', JSON.stringify(user));

    setData({ access_token, user });
  }, []);

  const storeData = useCallback(
    async (access_token: string) => {
      localStorage.setItem('@IndikeGanheCRM:token', access_token);

      getUser();
    },
    [getUser]
  );

  const signIn = useCallback(
    async ({ email, password }) => {
      const response = await api.post<AuthState>('/admin/auth/login', {
        email,
        password,
      });

      const { access_token } = response.data;

      await storeData(access_token);
    },
    [storeData]
  );

  const signOut = useCallback(() => {
    localStorage.removeItem('@IndikeGanheCRM:token');
    localStorage.removeItem('@IndikeGanheCRM:user');

    setData({} as AuthState);
  }, []);

  const updateUser = (user: UserDTO): void => {
    localStorage.setItem('@IndikeGanheCRM:user', JSON.stringify(user));
    Object.assign(data, { user });
  };

  const isLogged = useMemo(() => {
    return Object.keys(data).length > 0;
  }, [data]);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        signIn,
        signOut,
        updateUser,
        storeData,
        getUser,
        isLogged,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): AuthContextData => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export { useAuth, AuthProvider };
